/**
 * FP3-033 / FP3-033A — Student Goal Sheet (Display).
 *
 * Meeting-friendly goal sheet for iPad / projector. One goal per
 * "slide" with a large progress chart, calm metric row, and the
 * "What this goal looks like in practice" narrative. Prev / Next
 * navigation + window-day selector (30/60/90/180, default 90).
 *
 * Deliberately omitted (per FP3-033A spec):
 *   - Individual data-point table under the chart
 *   - Add Entry / edit controls
 *   - Provider dashboard chrome
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import {
  DisplayShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";
import DisplayGoalProgressCard from "@/components/print/DisplayGoalProgressCard";

function fmtPct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(0)}%`;
}

const WINDOW_CHOICES = [30, 60, 90, 180];

export default function DisplayStudentGoals() {
  const { studentId } = useParams();
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [student, setStudent] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [windowDays, setWindowDays] = useState(90);

  useEffect(() => {
    let cancelled = false;
    api.getStudent(studentId)
      .then((r) => {
        if (cancelled) return;
        setStudent(r.data);
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => { cancelled = true; };
  }, [studentId]);

  const goals = useMemo(
    () =>
      (student?.goals || []).filter(
        (g) => (g.status || "active") === "active"
      ),
    [student]
  );
  const name = student
    ? `${student.first_name} ${student.last_name}`
    : "Student";
  const provider = student?.provider_details?.[0];

  // Keep activeIdx in range as data loads.
  useEffect(() => {
    if (activeIdx >= goals.length && goals.length > 0) {
      setActiveIdx(0);
    }
  }, [goals.length, activeIdx]);

  const activeGoal = goals[activeIdx] || null;

  return (
    <DisplayShell
      title="Goal Sheet"
      subtitle={`${name}${student?.readable_id ? ` · ${student.readable_id}` : ""}${student?.school_name ? ` · ${student.school_name}` : ""}`}
      testId="display-student-goals"
    >
      <PrintAccessGate phase={phase} error={error}>
        {student && (
          <>
            <div className="fp-display-card" data-testid="display-goals-header">
              <div className="fp-display-grid-3">
                <div>
                  <div className="fp-display-label">Student</div>
                  <div className="fp-display-value">{name}</div>
                </div>
                <div>
                  <div className="fp-display-label">Grade · School</div>
                  <div className="fp-display-value">
                    {[student.grade && `Grade ${student.grade}`, student.school_name]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </div>
                </div>
                <div>
                  <div className="fp-display-label">Provider</div>
                  <div className="fp-display-value">
                    {provider
                      ? `${provider.first_name || ""} ${provider.last_name || ""}`.trim()
                      : "—"}
                  </div>
                </div>
              </div>
            </div>

            {goals.length === 0 ? (
              <div className="fp-display-card" data-testid="display-no-goals">
                <p style={{ margin: 0 }}>No active goals on file for this student.</p>
              </div>
            ) : (
              <>
                {/* Prev / Next navigation row */}
                <div
                  data-testid="display-goal-nav"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    maxWidth: 1100,
                    margin: "0 auto 18px",
                    padding: "10px 16px",
                    background: "#fff",
                    border: "1px solid var(--fp-print-line)",
                    borderRadius: 12,
                  }}
                >
                  <button
                    type="button"
                    data-testid="display-goal-prev"
                    onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
                    disabled={activeIdx === 0}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: "1px solid var(--fp-print-line)",
                      background: activeIdx === 0 ? "var(--fp-print-bg-soft)" : "#fff",
                      color: activeIdx === 0 ? "var(--fp-print-soft)" : "var(--fp-print-ink)",
                      cursor: activeIdx === 0 ? "default" : "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    <CaretLeft size={14} weight="bold" />
                    Previous
                  </button>
                  <div
                    data-testid="display-goal-counter"
                    style={{ fontSize: 15, fontWeight: 600, color: "var(--fp-print-ink)" }}
                  >
                    Goal {activeIdx + 1} of {goals.length}
                  </div>
                  <button
                    type="button"
                    data-testid="display-goal-next"
                    onClick={() => setActiveIdx((i) => Math.min(goals.length - 1, i + 1))}
                    disabled={activeIdx >= goals.length - 1}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: "1px solid var(--fp-print-line)",
                      background: activeIdx >= goals.length - 1 ? "var(--fp-print-bg-soft)" : "#fff",
                      color: activeIdx >= goals.length - 1 ? "var(--fp-print-soft)" : "var(--fp-print-ink)",
                      cursor: activeIdx >= goals.length - 1 ? "default" : "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    Next
                    <CaretRight size={14} weight="bold" />
                  </button>
                </div>

                {/* Active goal card with graph */}
                {activeGoal && (
                  <div
                    key={activeGoal.id}
                    className="fp-display-card"
                    data-testid={`display-goal-${activeGoal.id}`}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 10,
                        alignItems: "baseline",
                        marginBottom: 8,
                      }}
                    >
                      <h2 style={{ margin: 0 }}>Goal</h2>
                      {activeGoal.goal_area && (
                        <span
                          className="fp-print-pill"
                          style={{ fontSize: 12 }}
                          data-testid={`display-goal-area-${activeGoal.id}`}
                        >
                          {activeGoal.goal_area}
                        </span>
                      )}
                      {activeGoal.status && activeGoal.status !== "active" && (
                        <span
                          className="fp-print-pill"
                          style={{ fontSize: 12, textTransform: "capitalize" }}
                          data-testid={`display-goal-status-${activeGoal.id}`}
                        >
                          {activeGoal.status}
                        </span>
                      )}
                    </div>
                    <p
                      style={{ fontSize: 18, lineHeight: 1.55, margin: "4px 0 4px" }}
                      data-testid={`display-goal-text-${activeGoal.id}`}
                    >
                      {activeGoal.goal_text || "—"}
                    </p>

                    {/* Window selector — display-only, hidden in print */}
                    <div
                      data-testid="display-goal-window"
                      data-fp-no-print="true"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: 4,
                        background: "var(--fp-print-bg-soft)",
                        border: "1px solid var(--fp-print-line)",
                        borderRadius: 8,
                        marginTop: 10,
                      }}
                    >
                      {WINDOW_CHOICES.map((d) => (
                        <button
                          key={d}
                          type="button"
                          data-testid={`display-goal-window-${d}`}
                          onClick={() => setWindowDays(d)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 6,
                            border: "none",
                            background: windowDays === d ? "#fff" : "transparent",
                            color: windowDays === d ? "var(--fp-print-accent)" : "var(--fp-print-soft)",
                            boxShadow: windowDays === d ? "0 1px 2px rgba(31, 41, 55, .08)" : "none",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {d}d
                        </button>
                      ))}
                    </div>

                    {/* The graph itself */}
                    <DisplayGoalProgressCard
                      goal={activeGoal}
                      windowDays={windowDays}
                      testIdPrefix={`display-goal-progress-${activeGoal.id}`}
                    />

                    {/* Practice example — secondary, no overcrowding */}
                    <h3 style={{ marginTop: 22 }}>
                      What this goal looks like in practice
                    </h3>
                    <p
                      style={{
                        fontSize: 16,
                        lineHeight: 1.6,
                        margin: "4px 0 0",
                        color: "var(--fp-print-muted)",
                      }}
                      data-testid={`display-goal-practice-${activeGoal.id}`}
                    >
                      {activeGoal.practice_example || "No practice example added yet."}
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </PrintAccessGate>
    </DisplayShell>
  );
}
